import type {FC} from 'react'
import {useState} from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import {
  useDismissMemoryWriteMutation,
  useKeepMemoryToMyselfMutation,
  useApproveMemoryProposalMutation,
  useDeclineMemoryProposalMutation,
  useRestoreMemoryItemMutation,
  useRetractMemoryItemMutation,
  useShareMemoryItemMutation,
} from '@apis/aiCoreAPI'
import type {MemoryWriteNotice} from '@interfaces/alpinist/memoryConfirmation'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'

interface Props {
  notices: MemoryWriteNotice[]
  disabled?: boolean
}

/** What the card is showing, derived from the server plus this session's clicks. */
type CardState =
  | 'proposed' // nothing is stored yet — the only state where the claim does not exist
  | 'declined'
  | 'personal' // written for this person alone — the default, and the only one with a decision in it
  | 'kept'
  | 'removed'
  | 'shared' // organization scope, waiting on a colleague
  | 'merged' // shared into a claim a colleague had already taught
  | 'established'
  | 'orgOnly' // a tenant with per-user memory off: written organization-scoped
  | 'corroborated' // this write agreed with somebody else's claim
  | 'refused'
  | 'unavailable'

/** The server's `detail` on a 403, or null for any other failure. */
const forbiddenDetail = (error: unknown): string | null => {
  if (!error || typeof error !== 'object' || !('status' in error) || error.status !== 403)
    return null
  const data = 'data' in error ? (error.data as {detail?: unknown} | undefined) : undefined
  return typeof data?.detail === 'string' && data.detail ? data.detail : null
}

/**
 * What this turn recorded, stated by the server rather than by the answer above it.
 *
 * The reason it exists: told to remember an alias, Alpinist wrote "I'll treat this as
 * X in this chat going forward" — about a claim it had just shared with the entire
 * organization. Twice, in two environments. A person cannot be told who can see their
 * words by prose the model is free to phrase, so the scope comes from the row.
 *
 * PERSONAL FIRST. Everything `remember` writes is the author's alone; this card is
 * where it becomes shared, or does not. That inverts what the card is for. It used to
 * report a decision already taken and offer two ways to walk it back — which mattered,
 * because chat serves an unconfirmed organization claim to every colleague
 * immediately, so the walk-back was a race against the next person's question. Now the
 * safe state is the default and sharing is the deliberate act.
 *
 * It is still not a modal. Nothing here blocks the conversation, and the claim is
 * already in the only place it can do no harm.
 *
 * And it never leaves the conversation. Sharing, removing or keeping changes what the
 * card SAYS, never whether it is there: months later this line is the only thing that
 * answers "when did that become something the whole organization sees?". Every state
 * is read back from the server, so it reads the same on reload as it did the moment it
 * was clicked.
 */
const MemoryWriteNoticeCard: FC<Props> = ({notices, disabled}) => {
  const [t] = useCustomTranslation('alpinist')
  const [dismissWrite, {isLoading: dismissing}] = useDismissMemoryWriteMutation()
  const [retract, {isLoading: retracting}] = useRetractMemoryItemMutation()
  const [restore, {isLoading: restoring}] = useRestoreMemoryItemMutation()
  const [share, {isLoading: sharing}] = useShareMemoryItemMutation()
  const [keepToMyself, {isLoading: personalising}] = useKeepMemoryToMyselfMutation()
  const [approve, {isLoading: approving}] = useApproveMemoryProposalMutation()
  const [decline, {isLoading: declining}] = useDeclineMemoryProposalMutation()

  // Optimistic only: the server is the truth, this spares the user a wait for the
  // refetch. Anything the server already knows is read from the notice.
  const [acted, setActed] = useState<Record<string, CardState>>({})
  // Which cards the person reopened to act again after deciding. An acknowledgement
  // in haste is ordinary.
  const [reopened, setReopened] = useState<Record<string, boolean>>({})
  const [failed, setFailed] = useState<string | null>(null)
  // The server's own words for a refusal it is entitled to make (a 403), shown in
  // place of the generic failure line.
  const [failedDetail, setFailedDetail] = useState<string | null>(null)

  // Which proposal is open for editing, and what it has been edited to. An edit is
  // the strongest provenance available — whatever they type is theirs by
  // construction — so it is a first-class action here, not a fallback.
  const [editing, setEditing] = useState<Record<string, string>>({})
  const busy =
    dismissing || retracting || restoring || sharing || personalising || approving || declining
  if (!notices.length) return null

  const stateOf = (notice: MemoryWriteNotice): CardState => {
    if (notice.outcome === 'unavailable') return 'unavailable'
    // Nothing has been stored, and nothing will be unless this person says so. An
    // unanswered proposal is discarded — the inverse of what an unnoticed card used
    // to mean, and the whole point of the change.
    if (notice.outcome === 'proposed') return acted[notice.id] ?? 'proposed'
    if (notice.outcome === 'declined') return 'declined'
    if (notice.outcome === 'refused') return 'refused'
    if (notice.outcome === 'corroborated') return 'corroborated'

    const scope = notice.current_scope || notice.scope
    if (notice.status === 'retired') return 'removed'
    if (scope === 'organization') {
      if (notice.status === 'established') return 'established'
      // Written organization-scoped rather than moved there: this tenant has
      // per-user memory off, so the person was never offered the choice and must
      // not be told they made one.
      return notice.scope === 'organization' ? 'orgOnly' : 'shared'
    }
    // Personal, and the local verdict decides whether the actions are still open.
    return acted[notice.id] ?? (notice.acknowledged ? 'kept' : 'personal')
  }

  const run = async (
    notice: MemoryWriteNotice,
    action: () => Promise<unknown>,
    next: CardState,
  ): Promise<void> => {
    setFailed(null)
    setFailedDetail(null)
    try {
      await action()
      await dismissWrite({writeId: notice.id}).unwrap()
      setActed((prev) => ({...prev, [notice.id]: next}))
      setReopened((prev) => ({...prev, [notice.id]: false}))
    } catch (error) {
      // Leave the buttons live and say so. A failed removal that looked like a
      // success would be the worst outcome available here.
      setFailed(notice.id)
      // A 403 is a refusal with a reason, e.g. sharing without the permission to
      // confirm organization knowledge; say why rather than "something went wrong".
      setFailedDetail(forbiddenDetail(error))
    }
  }

  /** Put back a claim this card removed, and hand the choice back with it. */
  const undoRemoval = async (notice: MemoryWriteNotice): Promise<void> => {
    setFailed(null)
    setFailedDetail(null)
    if (!notice.item_id) return
    try {
      await restore({id: notice.item_id}).unwrap()
      // Back to undecided rather than to "kept": restoring returns the claim to the
      // state it was in when this card first asked.
      setActed((prev) => {
        const next = {...prev}
        delete next[notice.id]
        return next
      })
      setReopened((prev) => ({...prev, [notice.id]: true}))
    } catch {
      setFailed(notice.id)
    }
  }

  const titleFor = (notice: MemoryWriteNotice, state: CardState): string => {
    if (state === 'corroborated')
      return notice.taught_by
        ? t('memoryWrite.corroboratedTitle', {person: notice.taught_by})
        : t('memoryWrite.corroboratedTitleAnon')
    if (state === 'proposed') return t('memoryWrite.proposedTitle')
    if (state === 'declined') return t('memoryWrite.declinedTitle')
    if (state === 'established') return t('memoryWrite.establishedTitle')
    if (state === 'orgOnly') return t('memoryWrite.orgOnlyTitle')
    if (state === 'shared' || state === 'merged') return t('memoryWrite.sharedTitle')
    return t('memoryWrite.personalTitle')
  }

  /**
   * Who can see the claim, or nothing.
   *
   * Suppressed once the person has removed it: the line describes the audience of a
   * claim that is in use, so beside "Removed" it is a false statement sitting above
   * a true one.
   */
  const scopeFor = (state: CardState): string => {
    if (state === 'removed' || state === 'declined') return ''
    // Said in the future tense on purpose: it describes what approving would do, and
    // the claim does not exist yet.
    if (state === 'proposed') return t('memoryWrite.proposedScope')
    if (state === 'established') return t('memoryWrite.establishedScope')
    if (state === 'orgOnly') return t('memoryWrite.orgOnlyScope')
    if (state === 'shared' || state === 'merged') return t('memoryWrite.sharedScope')
    if (state === 'corroborated') return t('memoryWrite.corroboratedScope')
    return t('memoryWrite.personalScope')
  }

  /**
   * Why a write was refused, in the reader's language.
   *
   * Keyed on the gate's `check`, never on its `reason`. The reason is English
   * written AT THE MODEL -- "those are not the user's words -- a claim must be
   * quoted exactly from something they wrote in this conversation, not rewritten" --
   * and rendering it raw put developer English in front of a German user. It stays
   * on the row for support; this is what a person reads.
   */
  const refusalText = (notice: MemoryWriteNotice): string => {
    const byCheck: Record<string, string> = {
      provenance: 'memoryWrite.refusedProvenance',
      schema: 'memoryWrite.refusedSchema',
      acting: 'memoryWrite.refusedActing',
      anchors: 'memoryWrite.refusedAnchors',
    }
    const key = notice.check ? byCheck[notice.check] : undefined
    return t(key ?? 'memoryWrite.refusedFallback')
  }

  const written = notices.filter(
    (notice) => notice.outcome !== 'refused' && notice.outcome !== 'unavailable',
  )
  // Refusals are shown only when NOTHING was recorded this turn.
  //
  // The block exists for one failure: a person teaches Alpinist something, every
  // attempt is refused, and they are told nothing -- so they walk away believing it
  // worked. Seven consecutive refusals in DEV, all invisible.
  //
  // That is not what a mixed turn is. Asked to remember one sentence the model may
  // try three spans, have two rejected for being its own paraphrase rather than the
  // person's words, and get the third right. Production, first day: one claim
  // recorded and shared, two refusals shown underneath it. Nothing the person asked
  // for failed, and the card said "Couldn't record that" twice. Those refusals are
  // the model's own fumbling, and reporting them as the user's problem describes a
  // failure that did not happen.
  const refused = written.length ? [] : notices.filter((notice) => notice.outcome === 'refused')
  const unavailable = notices.filter((notice) => notice.outcome === 'unavailable')

  const actionsFor = (notice: MemoryWriteNotice, state: CardState): React.ReactNode => {
    const stop = disabled || busy || !notice.item_id

    if (state === 'personal')
      return (
        <Stack direction="row" spacing={1} sx={{mt: 0.25}} alignItems="center">
          {/* The point of the card. Outlined rather than text, and first, because
              everything else here is a way of NOT doing it. Hidden only when the
              server says this person may not share; without the flag it stays. */}
          {notice.can_share !== false && (
            <Button
              size="small"
              variant="outlined"
              disabled={stop}
              onClick={() =>
                run(notice, () => share({itemId: notice.item_id as string}).unwrap(), 'shared')
              }
              data-testid="memory-write-share"
            >
              {t('memoryWrite.share')}
            </Button>
          )}
          <Button
            size="small"
            variant="text"
            disabled={stop}
            onClick={() =>
              run(notice, () => retract({id: notice.item_id as string}).unwrap(), 'removed')
            }
            data-testid="memory-write-remove"
          >
            {t('memoryWrite.remove')}
          </Button>
          <Button
            size="small"
            variant="text"
            disabled={disabled || busy}
            onClick={() => run(notice, async () => undefined, 'kept')}
          >
            {t('memoryWrite.keep')}
          </Button>
          {busy && <CircularProgress size={14} />}
        </Stack>
      )

    if (state === 'proposed') {
      const edited = editing[notice.id]
      const isEditing = edited !== undefined
      return (
        <Stack spacing={1} sx={{mt: 0.5}}>
          {isEditing && (
            <TextField
              size="small"
              fullWidth
              multiline
              autoFocus
              value={edited}
              onChange={(e) => setEditing((prev) => ({...prev, [notice.id]: e.target.value}))}
              inputProps={{'data-testid': 'memory-write-edit-field'}}
            />
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              disabled={disabled || busy || (isEditing && !edited.trim())}
              onClick={() =>
                run(
                  notice,
                  () =>
                    approve({
                      writeId: notice.id,
                      ...(isEditing && edited.trim() !== notice.claim
                        ? {claim: edited.trim()}
                        : {}),
                    }).unwrap(),
                  'personal',
                )
              }
              data-testid="memory-write-approve"
            >
              {t('memoryWrite.approve')}
            </Button>
            {!isEditing && (
              <Button
                size="small"
                variant="text"
                disabled={disabled || busy}
                onClick={() => setEditing((prev) => ({...prev, [notice.id]: notice.claim}))}
                data-testid="memory-write-edit"
              >
                {t('memoryWrite.edit')}
              </Button>
            )}
            <Button
              size="small"
              variant="text"
              disabled={disabled || busy}
              onClick={() => run(notice, () => decline({writeId: notice.id}).unwrap(), 'declined')}
              data-testid="memory-write-decline"
            >
              {t('memoryWrite.decline')}
            </Button>
            {busy && <CircularProgress size={14} />}
          </Stack>
        </Stack>
      )
    }

    if (state === 'declined')
      return (
        <Typography variant="caption" color="text.secondary">
          {t('memoryWrite.doneDeclined')}
        </Typography>
      )

    if (state === 'shared' || state === 'orgOnly')
      return (
        <Stack direction="row" spacing={1} sx={{mt: 0.25}} alignItems="center">
          <Button
            size="small"
            variant="text"
            disabled={stop}
            onClick={() =>
              run(notice, () => keepToMyself({itemId: notice.item_id as string}).unwrap(), 'kept')
            }
            data-testid="memory-write-keep-to-myself"
          >
            {t('memoryWrite.keepToMyself')}
          </Button>
          <Button
            size="small"
            variant="text"
            disabled={stop}
            onClick={() =>
              run(notice, () => retract({id: notice.item_id as string}).unwrap(), 'removed')
            }
          >
            {t('memoryWrite.remove')}
          </Button>
        </Stack>
      )

    if (state === 'kept' || state === 'merged')
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {t(state === 'merged' ? 'memoryWrite.doneMerged' : 'memoryWrite.doneKept')}
          </Typography>
          <Button
            size="small"
            variant="text"
            disabled={disabled || busy}
            onClick={() => setReopened((prev) => ({...prev, [notice.id]: true}))}
          >
            {t('memoryWrite.change')}
          </Button>
        </Stack>
      )

    if (state === 'removed')
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {t('memoryWrite.doneRemoved')}
          </Typography>
          {/* Undo, not Change: reopening the buttons on a removed claim would offer
              Remove on something already removed. This puts it back first. */}
          <Button
            size="small"
            variant="text"
            disabled={stop}
            onClick={() => undoRemoval(notice)}
            data-testid="memory-write-undo"
          >
            {t('memoryWrite.undo')}
          </Button>
        </Stack>
      )

    // `established` and `corroborated` are settled between colleagues. Offering
    // Remove here would be overriding somebody else's agreement from a card in your
    // own chat; the knowledge table is where that decision belongs.
    return null
  }

  return (
    <Box sx={{mt: 1}} data-testid="memory-write-notice">
      {written.length > 0 && (
        <Alert severity="info" icon={false} sx={{py: 0.75}}>
          {written.map((notice) => {
            const state = reopened[notice.id] ? 'personal' : stateOf(notice)
            const scope = scopeFor(state)
            return (
              <Box key={notice.id} sx={{mb: 0.75}}>
                {/* The heading states the scope, so it cannot contradict the body —
                    it used to say "Added to your organization's knowledge" above a
                    line saying the claim was not shared with anybody. */}
                <AlertTitle sx={{fontSize: '0.8125rem', mb: 0.5}}>
                  {titleFor(notice, state)}
                </AlertTitle>
                {!(state === 'proposed' && editing[notice.id] !== undefined) && (
                  <Typography variant="body2" sx={{fontStyle: 'italic'}}>
                    {`“${notice.claim}”`}
                  </Typography>
                )}
                {state === 'proposed' && (
                  <Stack spacing={0.25} sx={{mt: 0.5}}>
                    {/* The local term is the lookup key — the word people actually
                        say. Deriving it from the claim's punctuation filed one real
                        production alias under a 61-character phrase nobody would
                        ever type, and reported success. */}
                    {notice.local_term && (
                      <Typography variant="caption" color="text.secondary">
                        {t('memoryWrite.proposedTerm', {
                          term: notice.local_term,
                          means: notice.means || '',
                        })}
                      </Typography>
                    )}
                    {!!notice.anchors?.length && (
                      <Stack direction="row" spacing={0.5} sx={{flexWrap: 'wrap', gap: 0.5}}>
                        {notice.anchors.map((anchor) => (
                          <Chip key={anchor.label} size="small" label={anchor.label} />
                        ))}
                      </Stack>
                    )}
                    {/* Where the claim came from. This is what provenance became
                        when it stopped refusing: a label somebody weighs, rather
                        than a string comparison standing in for their judgement. */}
                    <Typography variant="caption" color="text.secondary">
                      {notice.quote
                        ? t('memoryWrite.proposedFromYou', {quote: notice.quote})
                        : t('memoryWrite.proposedDerived')}
                    </Typography>
                  </Stack>
                )}
                {scope && (
                  <Typography variant="caption" color="text.secondary" sx={{display: 'block'}}>
                    {scope}
                  </Typography>
                )}
                {actionsFor(notice, state)}
                {failed === notice.id && (
                  <Typography variant="caption" color="error" sx={{display: 'block'}}>
                    {failedDetail ?? t('memoryWrite.failed')}
                  </Typography>
                )}
              </Box>
            )
          })}
        </Alert>
      )}

      {refused.length > 0 && (
        // Stated plainly rather than hidden. "I could not record that" is information
        // the person needs in order to say it differently; silence made them believe
        // the opposite of what happened.
        <Alert severity="warning" icon={false} sx={{mt: written.length ? 1 : 0, py: 0.75}}>
          <AlertTitle sx={{fontSize: '0.8125rem', mb: 0.5}}>
            {t('memoryWrite.refusedTitle')}
          </AlertTitle>
          {/* One line per DISTINCT reason. A turn that tried the same rewrite twice
              produced the identical sentence twice, which reads as two separate
              problems and is really one. */}
          {[...new Set(refused.map(refusalText))].map((text) => (
            <Typography key={text} variant="caption" color="text.secondary" sx={{display: 'block'}}>
              {text}
            </Typography>
          ))}
          {!refused.every((notice) => acted[notice.id] || notice.acknowledged) && (
            <Stack direction="row" spacing={1} sx={{mt: 0.5}}>
              <Button
                size="small"
                variant="text"
                disabled={disabled || busy}
                onClick={() =>
                  refused.forEach((notice) => run(notice, async () => undefined, 'kept'))
                }
              >
                {t('memoryWrite.ok')}
              </Button>
            </Stack>
          )}
        </Alert>
      )}

      {unavailable.length > 0 && (
        // Memory dying used to be indistinguishable from memory deciding there was
        // nothing worth keeping: both rendered nothing at all. Seen for real when a
        // stale worker held a renamed table — every read failed, and the answer came
        // back sounding confident with no sign anything was wrong.
        <Alert
          severity="warning"
          icon={false}
          sx={{mt: written.length || refused.length ? 1 : 0, py: 0.75}}
          data-testid="memory-write-unavailable"
        >
          <AlertTitle sx={{fontSize: '0.8125rem', mb: 0.5}}>
            {t('memoryWrite.unavailableTitle')}
          </AlertTitle>
          <Typography variant="caption" color="text.secondary">
            {t('memoryWrite.unavailableBody')}
          </Typography>
          {!unavailable.every((notice) => acted[notice.id] || notice.acknowledged) && (
            <Stack direction="row" spacing={1} sx={{mt: 0.5}}>
              <Button
                size="small"
                variant="text"
                disabled={disabled || busy}
                onClick={() =>
                  unavailable.forEach((notice) => run(notice, async () => undefined, 'kept'))
                }
              >
                {t('memoryWrite.ok')}
              </Button>
            </Stack>
          )}
        </Alert>
      )}
    </Box>
  )
}

export default MemoryWriteNoticeCard
